import { ReactElement, FC, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SvgAsset, ThemeProvider } from '@altermaind/web/ui';
import { svg as SvgInternal } from '../assets';
import { getModeTheme } from './state/selectors';
import { themeDark, themeLight } from './theme';

declare global {
  interface Window {
    accentLight: string;
    accentDark: string;
  }
}

type Props = {
  children: ReactElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assets: { images: any; icons: any };
  svg?: SvgAsset;
  primaryAsAccent?: boolean;
};

/**
 * AppThemeProvider
 * Mandatory to use the components exposed by @altermaind/web/ui
 * Fonts: <link rel="stylesheet" href="https://use.typekit.net/aam8sue.css" />
 */
export const PlatformThemeProvider: FC<Props> = ({
  children,
  assets,
  svg,
  primaryAsAccent = false,
}) => {
  const mode = useSelector(getModeTheme());
  const [accentColor, setAccentColor] = useState<{
    dark: string;
    light: string;
  }>();

  const theme = useMemo(() => {
    if (mode === 'light') {
      return {
        ...themeLight,
        colors: {
          ...themeLight.colors,
          primary: primaryAsAccent && accentColor ? accentColor?.light : themeLight.colors.primary,
          accent: accentColor?.light,
          // Use a lighter version for the background
          'accent/background': accentColor?.light.replace('1)', '0.2)'),
        },
      };
    }
    return {
      ...themeDark,
      colors: {
        ...themeDark.colors,
        primary: primaryAsAccent && accentColor ? accentColor?.dark : themeDark.colors.primary,
        accent: accentColor?.dark,
        // Use a lighter version for the background
        'accent/background': accentColor?.dark.replace('1)', '0.2)'),
      },
    };
  }, [accentColor, mode, primaryAsAccent]);

  useEffect(() => {
    const handleAccentChange = () => {
      const { accentDark, accentLight } = window;
      if (accentDark || accentLight) {
        const computedDark = accentDark || accentLight;
        const computedLight = accentLight || accentDark;
        setAccentColor({ dark: computedDark, light: computedLight });
      }
    };

    window.addEventListener('accent-change', handleAccentChange);

    return () => {
      window.removeEventListener('accent-change', handleAccentChange);
    };
  }, []);

  return (
    <ThemeProvider mode={mode} theme={theme as never} assets={assets} svg={svg || SvgInternal}>
      {children}
    </ThemeProvider>
  );
};
